/**
 * Transaction signing — Phase B of docs/REWRITE-PLAN.md.
 *
 * The shape of the whole thing, and the reason the console can be non-custodial:
 *
 *   1. the console serves account number and sequence  (/api/tenant/account)
 *   2. the wallet signs, OFFLINE, in the extension     (no key here, ever)
 *   3. the console relays the signed bytes             (/api/tenant/tx/broadcast)
 *   4. the console polls for inclusion                 (/api/tenant/tx/<hash>)
 *
 * Step 2 uses `SigningStargateClient.offline`, which signs against explicitly
 * supplied signer data instead of fetching it from a node. That is not a
 * workaround — it is what lets the browser sign without a direct connection to a
 * validator, which would need a CORS-open RPC port exposed to every tenant. The
 * relay exists so that port does not have to be.
 *
 * Nothing here can move funds on its own: every path ends in bytes the wallet
 * signed, and this module cannot produce a signature.
 */

import { toBase64 } from '@cosmjs/encoding'
import { type EncodeObject, Registry } from '@cosmjs/proto-signing'
import { defaultRegistryTypes, SigningStargateClient, type StdFee } from '@cosmjs/stargate'
// TxRaw lives in cosmjs-types, which is declared as a direct dependency rather
// than relied on via CosmJS's hoisting: a CosmJS bump could change the version
// under us, and a mismatched protobuf runtime fails at encode time.
import { TxRaw } from 'cosmjs-types/cosmos/tx/v1beta1/tx'

import { protocolRegistryTypes } from './protocol/registry'
import { ApiError } from './errors'
// Re-exported so callers that already import from here keep working; the
// definitions live in fees.ts precisely so they can be used without loading
// CosmJS.
export { feeFor, GAS_SEND, toMicroAmount } from './fees'
import { accountMismatch, answered, ensureChain, WalletError, type ChainConfig, type WalletKind, type WalletRequestOptions } from './wallet'

// Cosmos defaults plus the upstream Akash messages. Without those types registered,
// client.sign() throws on an unknown typeUrl — which is the good failure mode,
// but only if they are registered here rather than assumed.
export const registry = new Registry([...defaultRegistryTypes, ...protocolRegistryTypes])


export interface SignerData {
  /** bigint, because CosmJS 0.39 encodes it as a protobuf uint64.
   *
   *  Passing a `number` here type-errors but does NOT throw at runtime, and the
   *  signature it produces still looks valid — it is simply over the wrong sign
   *  document, which the chain rejects as `unauthorized`. That is the single most
   *  confusing way to fail, so the type is the guard and
   *  `test_signs_over_the_supplied_account_number` is the second one. */
  accountNumber: bigint
  sequence: number
  chainId: string
}

export interface SendResult {
  txhash: string
  accepted: boolean
  code: number
  rawLog: string
}

/** The Keplr/Leap sign options this module sets. A wallet's others are kept as they are. */
interface WalletSignOptions {
  preferNoSetFee?: boolean
  [option: string]: unknown
}

interface OfflineSignerSource {
  defaultOptions?: { sign?: WalletSignOptions | undefined; [option: string]: unknown } | undefined
  getOfflineSignerAuto?(chainId: string, signOptions?: WalletSignOptions): Promise<unknown>
  getOfflineSigner?(chainId: string, signOptions?: WalletSignOptions): unknown
}

type WalletWindow = typeof globalThis & {
  keplr?: OfflineSignerSource
  leap?: OfflineSignerSource
}

/**
 * Obtain the wallet's offline signer.
 *
 * `getOfflineSignerAuto` is preferred because it returns an *amino* signer for a
 * Ledger account and a direct signer otherwise. Choosing `getOfflineSigner`
 * outright would hand a Ledger user a signer their device cannot use, and the
 * failure would surface as an opaque rejection at signing time.
 *
 * The chain is registered with the wallet first (`ensureChain`): a restored
 * session reaches here without `connect` having run in this page's lifetime.
 *
 * Every signature the console asks for comes through here, so this is also
 * where the wallet is told to keep the fee `feeFor` built — see
 * `keepOurFee`.
 */
export async function offlineSigner(
  kind: WalletKind,
  chain: ChainConfig,
  win: WalletWindow = globalThis as WalletWindow,
  options: WalletRequestOptions = {},
): Promise<unknown> {
  const chainId = chain.chain_id
  const source = kind === 'keplr' ? win.keplr : win.leap
  if (!source) {
    throw new WalletError('not-installed', `${kind} is not available in this browser.`)
  }
  await ensureChain(kind, chain, win as Parameters<typeof ensureChain>[2], options)
  const signOptions = keepOurFee(source)
  if (source.getOfflineSignerAuto) return bounded(await answered(source.getOfflineSignerAuto(chainId, signOptions), kind, options), kind, options)
  if (source.getOfflineSigner) return bounded(source.getOfflineSigner(chainId, signOptions), kind, options)
  throw new WalletError('failed', `${kind} exposes no offline signer for ${chainId}.`)
}

/**
 * Tell the wallet to sign the fee the console built — none — instead of one
 * of its own, and return the per-signer options that say the same.
 *
 * CosmJS 0.39 broadcasts the auth info the wallet RETURNS from signing, not
 * the one it was handed, and Keplr and Leap both replace the fee with their
 * own estimate unless asked for `preferNoSetFee`. A wallet that stored the old
 * 0.01/0.025/0.04 gas-price steps would then sign 0.025 uddt per gas whatever
 * `feeFor` built, and suggesting the chain again does not reliably refresh
 * steps a wallet has already stored.
 *
 * So it is asked twice. Keplr reads `defaultOptions.sign` at signing time;
 * Leap documents sign options only as the second argument of
 * `getOfflineSigner`, and Keplr accepts them there too. The wallet can still
 * show the fee and let the person change it; this only stops it from
 * substituting one unasked.
 */
function keepOurFee(source: OfflineSignerSource): WalletSignOptions {
  try {
    source.defaultOptions = { ...source.defaultOptions, sign: { ...source.defaultOptions?.sign, preferNoSetFee: true } }
  } catch {
    // An injected object that refuses the assignment still gets the
    // per-signer option returned below.
  }
  return { preferNoSetFee: true }
}

/** The signer methods that raise an extension prompt, or wait behind one. */
const PROMPTING = new Set<PropertyKey>(['getAccounts', 'signDirect', 'signAmino'])

/**
 * The same signer, with each prompting call bounded like every other wallet
 * call: a skipped approval here once left the rental pages busy until reload.
 *
 * Bounding a transaction signature is safe because the console broadcasts the
 * signed bytes itself. A late approval, after the bound has fired, resolves a
 * promise nobody is awaiting any more, so it is dropped and never broadcast.
 */
function bounded(signer: unknown, kind: WalletKind, options: WalletRequestOptions): unknown {
  if (typeof signer !== 'object' || signer === null) return signer
  return new Proxy(signer, {
    get(target, prop, receiver) {
      const value: unknown = Reflect.get(target, prop, receiver)
      if (!PROMPTING.has(prop) || typeof value !== 'function') return value
      return (...args: unknown[]) => answered(Promise.resolve(value.apply(target, args)), kind, options)
    },
  })
}

export function sendMessage(from: string, to: string, amount: string, denom: string): EncodeObject {
  return {
    typeUrl: '/cosmos.bank.v1beta1.MsgSend',
    value: { fromAddress: from, toAddress: to, amount: [{ denom, amount }] },
  }
}

/**
 * Sign messages offline and return the base64 bytes ready to relay.
 *
 * Split out from `submit` so the signing step can be tested with a real key
 * against a real signer, without any HTTP.
 */
export async function signToBase64(
  signer: unknown,
  address: string,
  messages: EncodeObject[],
  fee: StdFee,
  memo: string,
  signerData: SignerData,
): Promise<string> {
  // The console restores a remembered wallet from the server session without
  // asking the extension (asking a locked Keplr opens its unlock window on
  // every page load). So the address here is the session's, and the wallet may
  // since have been switched to another account: say so, rather than let
  // CosmJS fail with "Failed to retrieve account from signer".
  const accounts = await (signer as { getAccounts?: () => Promise<readonly { address: string }[]> }).getAccounts?.()
  if (accounts && !accounts.some((account) => account.address === address)) {
    throw accountMismatch(accounts[0]?.address, address)
  }
  // `offline` signs against the signer data we pass in rather than querying a
  // node — see the module comment.
  const client = await SigningStargateClient.offline(
    signer as Parameters<typeof SigningStargateClient.offline>[0],
    { registry },
  )
  const txRaw = await client.sign(address, messages, fee, memo, signerData)
  return toBase64(TxRaw.encode(txRaw).finish())
}

/** Minimal surface of the console API this module needs, injected for testing. */
export interface TxApi {
  account(address: string): Promise<{ account_number: string | null; sequence: string | null }>
  broadcast(txBytes: string): Promise<{
    accepted: boolean
    code: number
    txhash: string
    raw_log: string
  }>
  status(hash: string): Promise<{ found: boolean; succeeded?: boolean; code?: number; raw_log?: string }>
}

/**
 * The full path: read the account, sign, relay, report.
 *
 * Returns as soon as the chain has accepted or rejected the transaction. Note
 * what "accepted" does and does not mean — a sync broadcast returns after CheckTx,
 * which is before the transaction is in a block, so `waitForInclusion` is a
 * separate step rather than folded in here.
 */
export async function submit(
  args: {
    signer: unknown
    address: string
    messages: EncodeObject[]
    fee: StdFee
    memo?: string
    chainId: string
    /** Called after wallet signing succeeds and immediately before relay. */
    onBroadcast?: () => void
    /** Revalidate source reservations after wallet signing, before any paid relay. */
    beforeBroadcast?: () => Promise<void>
  },
  api: TxApi,
): Promise<SendResult> {
  const info = await api.account(args.address)
  if (info.account_number === null || info.sequence === null) {
    // Signing with a guessed account number produces a valid signature over the
    // wrong document, which the chain rejects as unauthorized — a confusing way
    // to learn the account has never been funded.
    throw new ApiError(
      400,
      'This account does not exist on chain yet.',
      'It needs to receive funds before it can send a transaction.',
    )
  }

  const txBytes = await signToBase64(
    args.signer,
    args.address,
    args.messages,
    args.fee,
    args.memo ?? '',
    {
      accountNumber: BigInt(info.account_number),
      sequence: Number(info.sequence),
      chainId: args.chainId,
    },
  )

  await args.beforeBroadcast?.()
  args.onBroadcast?.()
  const result = await api.broadcast(txBytes)
  return {
    txhash: result.txhash,
    accepted: result.accepted,
    code: result.code,
    rawLog: result.raw_log,
  }
}

/**
 * Poll until the transaction is in a block.
 *
 * `found: false` is the normal state for the first second or two, so it is not an
 * error and does not end the loop. Giving up after `attempts` reports a timeout
 * rather than a failure, because the transaction may still land — telling a user
 * their transfer failed when it is merely slow is worse than telling them it is
 * unconfirmed.
 */
export async function waitForInclusion(
  hash: string,
  api: TxApi,
  { attempts = 20, intervalMs = 1500, sleep = defaultSleep } = {},
): Promise<{ included: boolean; succeeded: boolean; code: number; rawLog: string }> {
  for (let i = 0; i < attempts; i++) {
    const status = await api.status(hash)
    if (status.found) {
      return {
        included: true,
        succeeded: status.succeeded ?? false,
        code: status.code ?? 0,
        rawLog: status.raw_log ?? '',
      }
    }
    if (i < attempts - 1) await sleep(intervalMs)
  }
  return { included: false, succeeded: false, code: 0, rawLog: '' }
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
